/**
 * MFE Registry Configuration
 * 
 * Centralized configuration for all Micro Frontends (MFEs) in the Lore ecosystem.
 * This registry defines:
 * - Remote URLs for each environment (localhost, nonprod, prod)
 * - Module Federation scope and module names
 * - Props to pass to each MFE
 * 
 * Pattern inspired by mfe-bridge registry but adapted for web client.
 * 
 * HOW TO ADD A NEW MFE:
 * 1. Add entry to mfeRegistry object below
 * 2. Define config (remoteUrl, scope, module, optional path)
 * 3. Define props function that returns MFE-specific props
 * 4. Create a route component in src/components/mfe/
 * 5. Add route to App.tsx
 * 6. Add navigation link to Sidebar.tsx
 */

/**
 * MFE Configuration Interface
 * Defines the structure for each MFE's configuration
 */
export interface MfeConfig {
  remoteUrl: {
    localhost: string;    // URL when running MFE locally (e.g., http://localhost:3002)
    nonprod: string;      // Non-production deployment URL
    prod: string;         // Production deployment URL
  };
  scope: string;          // Module Federation scope name (must match MFE's webpack config)
  module: string;         // Module Federation exposed module name (e.g., './Rewards')
  path?: string;          // Optional: Base path for routing (e.g., '/rewards')
  /** Optional: remoteEntry filename or path (default 'remoteEntry.js'). Use if the MFE serves it elsewhere (e.g. 'static/remoteEntry.js'). */
  remoteEntryFileName?: string;
}

/**
 * MFE Props Context Interface
 * Data passed to getMfeProps() to generate MFE-specific props
 */
export interface MfePropsContext {
  accessToken: string;     // JWT access token for API calls
  refUserId: string | number; // Lore internal user ID
  onLogout: () => void;    // Callback when user logs out from MFE
}

/**
 * MFE Registry
 * 
 * Central registry of all MFEs available in the application.
 * Each MFE has:
 * - config: Deployment URLs, Module Federation configuration
 * - props: Function that generates props to pass to the MFE
 * 
 * IMPORTANT: Props structure must match what each MFE expects!
 * Review each MFE's App.tsx to see its prop interface.
 */
export const mfeRegistry: Record<string, {
  config: MfeConfig;
  props: (ctx: MfePropsContext) => any;
}> = {
  /**
   * Rewards MFE (Post-Auth)
   * 
   * Main rewards tracking and redemption interface.
   * Users can view points balance, history, and redeem rewards.
   * 
   * Props: CommonMfeProps (hostAuthResult, externalRefUserId, onGetAccessToken, onHostLogout)
   * Auth: Requires authenticated user
   */
  Rewards: {
    config: {
      remoteUrl: {
        localhost: 'http://localhost:3002',
        nonprod: 'https://rewards-non-prod.lorehealthcare.com',
        prod: 'https://rewards-prod.lorehealthcare.com',
      },
      scope: 'Rewards',
      module: './Rewards',
      path: '/rewards',
    },
    props: (ctx) => {
      // When host is app (non-prod/prod), use same-origin proxy for icons to avoid cross-origin 403/ORB
      const isAppHost =
        typeof window !== 'undefined' &&
        (window.location.hostname === 'app.lorehealthcare.com' || window.location.hostname === 'app.lore.co');
      const env = process.env.REACT_APP_ENV || process.env.REACT_APP_MFE_ENV || '';
      const rewardsProxyPath = env === 'prod' || env === 'production' ? '/mfe-proxy/rewards-prod' : '/mfe-proxy/rewards';
      const assetBaseUrl =
        isAppHost && typeof window !== 'undefined'
          ? `${window.location.origin}${rewardsProxyPath}`
          : undefined;
      return {
        // Legacy: Some MFEs expect hostAuthResult object
        hostAuthResult: { accessToken: ctx.accessToken },
        // User ID in MFE-specific naming
        externalRefUserId: ctx.refUserId,
        // CRITICAL: Function to get fresh access token
        onGetAccessToken: () => ctx.accessToken,
        onHostLogout: ctx.onLogout,
        // Optional: base URL for static assets (icons). If Rewards MFE uses this, avoids 403/ORB from cross-origin.
        ...(assetBaseUrl && { assetBaseUrl, publicPath: assetBaseUrl }),
      };
    },
  },

  /**
   * Account Creation MFE (Pre-Auth)
   * 
   * User onboarding and account creation flow.
   * Collects email, consents, and creates onboarding session.
   * 
   * Props: onModuleFinished, onAccountCreationLoaded
   * Auth: Does NOT require authentication (pre-auth flow)
   * 
   * IMPORTANT: This MFE has different props than post-auth MFEs!
   */
  AccountCreation: {
    config: {
      remoteUrl: {
        localhost: 'http://localhost:3001',
        nonprod: 'https://onboarding-test.lorehealthcare.com',
        prod: 'https://onboarding.lorehealthcare.com',
      },
      scope: 'RemoteOnboarding',
      module: './Onboarding',
      path: '/account-creation',
    },
    props: (_ctx) => ({
      /**
       * Callback when account creation completes
       * 
       * @param mfeType - Type of MFE completing (e.g., 'onboarding')
       * @param data - Data from MFE (e.g., { email, oe_user_session_id })
       * 
       * Handle the completion:
       * - Navigate to next step
       * - Store session data
       * - Trigger backend account creation
       */
      onModuleFinished: (_mfeType: string, _data: any) => {
        // TODO: Implement account creation completion handler
        // - Could navigate to password creation
        // - Could call backend API to finalize account
        // - Could show success message
      },
      
      /**
       * Callback when MFE loads successfully
       * Used for analytics, logging, or UI updates
       */
      onAccountCreationLoaded: () => {},
    }),
  },

  /**
   * LoreBot MFE (Post-Auth)
   * 
   * Conversational AI interface for health-related conversations.
   * 
   * Props: CommonMfeProps + specific LoreBot callbacks
   * Auth: Requires authenticated user
   * 
   * IMPORTANT: LoreBot expects specific prop types:
   * - externalRefUserId: number (not string!)
   * - onGetAccessToken: () => Promise<string> (async!)
   * - onLoreBotLoaded: (sendMessageFunction: any) => void
   * - onRedirectToPost: (postId: number) => void
   */
  LoreBot: {
    config: {
      remoteUrl: {
        localhost: 'http://localhost:3001/services/lorebot-ui',
        nonprod: 'https://lorebot-mfe-nonprod.lorehealthcare.io',
        prod: 'https://lorebot-mfe.lorehealthcare.com',
      },
      scope: 'RemoteLoreBot',
      module: './LoreBotChat',
      path: '/lorebot',
    },
    props: (ctx) => ({
      // LoreBot expects externalRefUserId as a NUMBER
      externalRefUserId: typeof ctx.refUserId === 'string' 
        ? parseInt(ctx.refUserId, 10) 
        : ctx.refUserId,
      
      // CRITICAL: LoreBot expects onGetAccessToken to return a PROMISE
      onGetAccessToken: async () => ctx.accessToken,
      
      // Callback when LoreBot finishes loading (receives sendMessage function)
      onLoreBotLoaded: () => {},
      
      // Callback for draft editing (used for post composition)
      onDraftEdit: () => {},
      
      // Callback to redirect to a community post (handled by App.tsx LORE_BRIDGE_NAVIGATION / getRoute)
      onRedirectToPost: () => {},
      
      // Logout callback
      onHostLogout: ctx.onLogout,
    }),
  },

  /**
   * Community MFE (Post-Auth)
   * 
   * Social features, posts, and community interactions.
   * 
   * Props: CommonMfeProps
   * Auth: Requires authenticated user
   */
  Community: {
    config: {
      remoteUrl: {
        localhost: 'http://localhost:3005',
        nonprod: 'https://lore-community-mfe-nonprod.storage.googleapis.com',
        prod: 'https://lore-community-mfe.storage.googleapis.com',
      },
      scope: 'LoreCommunityScope',
      module: './LoreCommunityModule',
      path: '/community',
    },
    props: (ctx) => ({
      hostAuthResult: { accessToken: ctx.accessToken },
      externalRefUserId: ctx.refUserId,
      onGetAccessToken: () => ctx.accessToken,
      onHostLogout: ctx.onLogout,
    }),
  },

  /**
   * Diagnostics MFE (Post-Auth)
   * 
   * User settings, profile, and diagnostic tools.
   * 
   * Props: CommonMfeProps + onDiagnosticsLoaded
   * Auth: Requires authenticated user
   */
  Diagnostics: {
    config: {
      remoteUrl: {
        localhost: 'http://localhost:3003',
        nonprod: 'https://diagnostics-mfe-nonprod.lorehealthcare.io',
        prod: 'https://diagnostics-mfe.lorehealthcare.com',
      },
      scope: 'diagnostics',
      module: './DiagnosticsApp',
      path: '/diagnostics',
    },
    props: (ctx) => ({
      hostAuthResult: { accessToken: ctx.accessToken },
      externalRefUserId: ctx.refUserId,
      onGetAccessToken: () => ctx.accessToken,
      onHostLogout: ctx.onLogout,
      onDiagnosticsLoaded: () => {},
    }),
  },

  /**
   * Streaks MFE (Post-Auth)
   * 
   * Daily activity tracking and streaks.
   * 
   * Props: CommonMfeProps + onStreaksLoaded
   * Auth: Requires authenticated user
   */
  Streaks: {
    config: {
      remoteUrl: {
        localhost: 'http://localhost:3011',
        nonprod: 'https://lore-streaks-ui-nonprod.web.app',
        prod: 'https://lore-streaks-ui-prod.web.app',
      },
      scope: 'RemoteStreaks',
      module: './StreaksPage',
      path: '/streaks',
    },
    props: (ctx) => ({
      hostAuthResult: { accessToken: ctx.accessToken },
      externalRefUserId: ctx.refUserId,
      onGetAccessToken: () => ctx.accessToken,
      onHostLogout: ctx.onLogout,
      onStreaksLoaded: () => {},
    }),
  },

  /**
   * Verification (UCS) MFE (Post-Auth)
   * 
   * User verification flow for identity confirmation.
   * 
   * Props: CommonMfeProps
   * Auth: Requires authenticated user
   */
  Verification: {
    config: {
      remoteUrl: {
        localhost: 'http://localhost:3010',
        nonprod: 'https://lore-verification-service-app-eex6i6xwzq-uc.a.run.app',
        prod: 'https://verification.lorehealthcare.com',
      },
      scope: 'RemoteOnboarding',
      module: './UCS',
      path: '/verification',
    },
    props: (ctx) => ({
      hostAuthResult: { accessToken: ctx.accessToken },
      externalRefUserId: ctx.refUserId,
      onGetAccessToken: () => ctx.accessToken,
      onHostLogout: ctx.onLogout,
    }),
  },

  /**
   * FAQ/Help MFE (Post-Auth)
   * 
   * Help center and frequently asked questions.
   * Uses the same streaks-ui deployment with FAQ module.
   * 
   * Props: CommonMfeProps
   * Auth: Requires authenticated user
   */
  FAQ: {
    config: {
      remoteUrl: {
        localhost: 'http://localhost:3009',
        nonprod: 'https://lore-streaks-ui-nonprod.web.app',
        prod: 'https://lore-streaks-ui-prod.web.app',
      },
      scope: 'RemoteStreaks',
      module: './FAQPage',
      path: '/help',
    },
    props: (ctx) => ({
      hostAuthResult: { accessToken: ctx.accessToken },
      externalRefUserId: ctx.refUserId,
      onGetAccessToken: () => ctx.accessToken,
      onHostLogout: ctx.onLogout,
    }),
  },

  /**
   * User Preferences MFE (Post-Auth)
   * 
   * User preferences for notifications and privacy settings.
   * Supports initialPath prop to navigate to specific sections.
   * 
   * Props: CommonMfeProps + initialPath + notificationStatus + onModuleFinished
   * Auth: Requires authenticated user
   */
  UserPreferences: {
    config: {
      remoteUrl: {
        localhost: 'http://localhost:3013',
        nonprod: 'https://user-preferences-nonprod.lorehealthcare.io',
        prod: 'https://user-preferences.lore.co',
      },
      scope: 'UserPreferences',
      module: './UserPreferences',
      path: '/preferences',
    },
    props: (ctx) => ({
      hostAuthResult: { accessToken: ctx.accessToken },
      externalRefUserId: ctx.refUserId,
      onGetAccessToken: () => ctx.accessToken,
      onHostLogout: ctx.onLogout,
      notificationStatus: null, // Optional notification status
      onModuleFinished: () => {},
      // dataPackage with initialPath will be passed when loading the MFE component
    }),
  },

  // TODO: Add more MFEs as needed:
  // - Diagnostics (settings, profile)
  // - etc.
};

/**
 * Get MFE URL for Current Environment
 * 
 * Determines which URL to use based on environment variables.
 * 
 * Environment detection:
 * - REACT_APP_MFE_ENV=local → localhost (for MFE development)
 * - REACT_APP_MFE_ENV=nonprod OR default → nonprod (deployed MFEs)
 * - REACT_APP_MFE_ENV=prod → prod (deployed MFEs)
 * 
 * By default, uses nonprod deployed MFEs even when running web client locally.
 * This allows you to develop the web client without running MFEs locally.
 * 
 * @param mfeName - Name of MFE from registry (e.g., 'Rewards')
 * @returns Full URL to MFE (e.g., 'https://rewards-non-prod.lorehealthcare.com')
 * @throws Error if MFE not found in registry
 */
export function getMfeUrl(mfeName: string): string {
  const config = mfeRegistry[mfeName]?.config;
  if (!config) {
    throw new Error(`MFE "${mfeName}" not found in registry`);
  }

  // Use REACT_APP_MFE_ENV to control which MFEs to load
  // Defaults to 'nonprod' so local web client development uses deployed MFEs
  const mfeEnv = process.env.REACT_APP_MFE_ENV || 'nonprod';

  let baseUrl: string;
  if (mfeEnv === 'local') {
    baseUrl = config.remoteUrl.localhost;
  } else if (mfeEnv === 'nonprod') {
    baseUrl = config.remoteUrl.nonprod;
  } else {
    baseUrl = config.remoteUrl.prod;
  }

  return baseUrl;
}

/**
 * Get MFE Props for Specific MFE
 * 
 * Generates props object to pass to the MFE component.
 * Props are MFE-specific and defined in the registry.
 * 
 * @param mfeName - Name of MFE from registry
 * @param context - Context data (token, userId, callbacks)
 * @returns Props object to spread onto MFE component
 * @throws Error if MFE not found in registry
 * 
 * @example
 * const props = getMfeProps('Rewards', {
 *   accessToken: 'jwt-token',
 *   refUserId: '12345',
 *   onLogout: () => console.log('logout'),
 * });
 * // Returns: { hostAuthResult: {...}, externalRefUserId: '12345', ... }
 */
export function getMfeProps(mfeName: string, context: MfePropsContext): any {
  const mfe = mfeRegistry[mfeName];
  if (!mfe) {
    throw new Error(`MFE "${mfeName}" not found in registry`);
  }
  return mfe.props(context);
}

/**
 * Get All MFE Names
 * 
 * Returns list of all registered MFE names.
 * Useful for generating navigation menus dynamically.
 * 
 * @returns Array of MFE names
 */
export function getAllMfeNames(): string[] {
  return Object.keys(mfeRegistry);
}

/**
 * Check if MFE Exists
 * 
 * @param mfeName - Name to check
 * @returns true if MFE is registered
 */
export function isMfeRegistered(mfeName: string): boolean {
  return mfeName in mfeRegistry;
}

